import { ChevronRight, Play } from 'lucide-react';
import type { IconComponent } from '../data/icons';

// A position in the Library / Favorites index: one row per category, with the
// icon in a tinted square, the name, a line saying how much is inside, and —
// while closed — a peek at the first few clips. Open, the row's grid or list
// drops in directly beneath it, with no frame around it.
interface CategoryCardProps {
  label: string;
  /** Unused since the index-row redesign; kept so older callers still compile. */
  color?: string;
  open: boolean;
  onToggle: () => void;
  icon?: IconComponent;
  /** Under the name: "24 techniques · 11 creators". */
  meta?: string;
  /** Thumbnails of the first clips, shown while the row is closed. */
  peek?: string[];
  children?: React.ReactNode;
  /** Desktop-only: open every video in this group as a grid of PiP players. */
  onOpenAll?: () => void;
}

export default function CategoryCard({ label, open, onToggle, icon: Icon, meta, peek = [], children, onOpenAll }: CategoryCardProps) {
  return (
    <div className={`category-card${open ? ' category-card--open' : ''}`}>
      {/* The whole row toggles; the button inside is the accessible control
          and stops the click reaching the row so it doesn't toggle twice. */}
      <div className="category-card-header" onClick={onToggle}>
        <button className="category-card-toggle" onClick={(e) => { e.stopPropagation(); onToggle(); }} aria-expanded={open}>
          <span className="category-card-icon" aria-hidden="true">{Icon && <Icon size={18} />}</span>
          <span className="category-card-text">
            <span className="category-card-label">{label}</span>
            {meta && <span className="category-card-meta">{meta}</span>}
          </span>
          {!open && peek.length > 0 && (
            <span className="category-card-peek" aria-hidden="true">
              {peek.map((src) => <img key={src} src={src} alt="" loading="lazy" decoding="async" />)}
            </span>
          )}
        </button>
        {open && onOpenAll && (
          <button
            className="category-card-playall"
            onClick={(e) => { e.stopPropagation(); onOpenAll(); }}
            aria-label={`Open all ${label} videos in picture-in-picture`}
            title="Open all videos"
          >
            <Play size={11} fill="currentColor" />
            Play all
          </button>
        )}
        <span className="accordion-arrow" aria-hidden="true"><ChevronRight size={16} /></span>
      </div>
      {open && children && (
        <div className="techniques-list">{children}</div>
      )}
    </div>
  );
}
